import { AvailableCountryISO3 } from "@/types";
import type { ApplyRecommendationsReport, BerWowSettings, Rating } from "@/types";
import { hasNoFossilHeatingFuelCodes } from "./ber-rating-fuels";
import { BerRatingScales, LegacyIrlBerRatings, SimplifiedIrlBerRatings } from "./ber-rating-scales";
import { BerwowEvent, on } from "./event";

export { SimplifiedIrlBerRatings } from "./ber-rating-scales";

type RatingSourceFormat = "legacy" | "new" | "unknown";

export interface RatingDisplay {
  code: string;
  color?: string;
  isPotentialA0: boolean;
  annotation?: string;
  annotationTooltip?: string;
}

const IRL_A0_RATING_CODE = "A0";
const LEGACY_IRL_SUB_RATING_PATTERN = /^([A-E])\d+$/i;
const HEX_COLOR_PATTERN = /^#([0-9A-F]{3}){1,2}$/i;
const TRANSPARENT_RATING_ALPHA = 0.5;
const POTENTIAL_A0_MAX_PRIMARY_ENERGY = 42;
const POTENTIAL_A0_ANNOTATION = "Potential A0";
const POTENTIAL_A0_TOOLTIP = "Indicative only. Official A0 status must be confirmed by the competent authority.";
const IRL_NUMERIC_RATING_EXCLUDED_CODES = new Set([IRL_A0_RATING_CODE]);

let activeCountry = AvailableCountryISO3.IRL;
export let BerRatings = BerRatingScales[AvailableCountryISO3.IRL];
function setBerRatings(country: AvailableCountryISO3) {
  activeCountry = country;
  BerRatings = BerRatingScales[country];
}
on(BerwowEvent.SettingsLoaded, (settings: BerWowSettings) => {
  if (settings.country) setBerRatings(settings.country);
});

export function getRatingColor(code?: string, transparent?: boolean) {
  const color = BerRatings.get(getDisplayRatingCode(code))?.color;
  return transparent ? getTransparentRatingColor(color) : color;
}

export function getWidthPercentage(value: number): number {
  const maxValue = getBerMaxValue();
  const firstValue = getBerFirstValue();
  const percentage = (value / maxValue) * 100;
  return firstValue && firstValue === maxValue ? 100 - percentage : percentage;
}

export function calculatePercentage(level: number): number {
  const levels = [0, ...getSortedRatingLevels()];
  const levelIndex = levels.indexOf(level);
  if (levelIndex === -1) return -1;
  if (levelIndex === 0) return 0;

  const displayLevels = getDisplayRatingLevels(levels);
  const range = displayLevels[displayLevels.length - 1] - displayLevels[0];
  const displayLevel = displayLevels[levelIndex];
  const previousLevel = displayLevels[levelIndex - 1];
  return range > 0 ? ((displayLevel - previousLevel) / range) * 100 : 0;
}

export function getBerMaxValue() {
  return Math.max(...getDisplayRatingLevels(getSortedRatingLevels()));
}
export function getBerFirstValue() {
  const [firstRatingCode] = getSortedRatingCodes();
  return BerRatings.get(firstRatingCode)?.level;
}

export function getRating(value: number): string {
  const entries = getNumericRatingEntries();
  const matchingEntry = entries.find(([, ratingInfo]) => value <= ratingInfo.level);
  return matchingEntry?.[0] ?? entries[entries.length - 1][0];
}

export function getDisplayRatingCode(code?: string): string {
  const normalizedCode = normalizeRatingCode(code);
  return isIrlActive()
    ? simplifyIrlBerRatingCode(normalizedCode)
    : normalizedCode;
}

export function getIrlRatingSourceFormat(code?: string): RatingSourceFormat {
  const normalizedCode = normalizeRatingCode(code);
  if (!normalizedCode) return "unknown";
  if (isLegacyIrlSubRating(normalizedCode)) return "legacy";
  if (SimplifiedIrlBerRatings.has(normalizedCode)) return "new";
  if (LegacyIrlBerRatings.has(normalizedCode)) return "legacy";
  return "unknown";
}

export function simplifyIrlBerRatingCode(code?: string): string {
  const normalizedCode = normalizeRatingCode(code);
  if (!normalizedCode) return "";
  if (SimplifiedIrlBerRatings.has(normalizedCode)) return normalizedCode;

  const legacySubRatingMatch = normalizedCode.match(LEGACY_IRL_SUB_RATING_PATTERN);
  if (legacySubRatingMatch) return legacySubRatingMatch[1];

  return normalizedCode;
}

export function simplifyIrlBerRating(rating: Rating): Rating {
  return {
    ...rating,
    code: simplifyIrlBerRatingCode(rating.code),
  };
}

export function getRatingDisplay(
  rating?: Rating | null,
  dwelling?: string | object | null
): RatingDisplay {
  const sourceCode = getRatingSourceCode(rating);
  const sourceFormat = isIrlActive()
    ? getIrlRatingSourceFormat(sourceCode)
    : "unknown";
  const code = getReportRatingDisplayCode(rating, sourceCode, sourceFormat);
  const isPotentialA0 = !!rating && canShowPotentialA0(
    rating,
    sourceFormat,
    dwelling
  );

  return {
    code,
    color: getRatingColor(code) || undefined,
    isPotentialA0,
    annotation: isPotentialA0 ? rating?.annotation ?? POTENTIAL_A0_ANNOTATION : undefined,
    annotationTooltip: isPotentialA0 ? rating?.annotationTooltip ?? POTENTIAL_A0_TOOLTIP : undefined,
  };
}

export function normalizeReportBerRatings(report?: ApplyRecommendationsReport | null) {
  if (!report || !isIrlActive() || !report.beforeRating || !report.afterRating) return report;

  report.beforeRating = normalizeReportBerRating(
    report.beforeRating,
    report.resultsAnalysis?.dwellingBefore
  );
  report.afterRating = normalizeReportBerRating(
    report.afterRating,
    report.resultsAnalysis?.dwellingAfter
  );

  return report;
}

export function getSimplifiedIrlBerRatingColor(code?: string) {
  return SimplifiedIrlBerRatings.get(simplifyIrlBerRatingCode(code))?.color;
}

function normalizeReportBerRating(
  rating: Rating,
  dwelling?: string | object | null
): Rating {
  if (isAchievedA0Rating(rating)) {
    return {
      ...rating,
      isPotentialA0: false,
      annotation: undefined,
      annotationTooltip: undefined,
    };
  }

  const sourceCode = getRatingSourceCode(rating);
  const sourceFormat = getIrlRatingSourceFormat(sourceCode);
  const display = getRatingDisplay(rating, dwelling);

  if (sourceFormat !== "legacy") {
    return display.isPotentialA0
      ? {
          ...rating,
          isPotentialA0: display.isPotentialA0,
          annotation: display.annotation,
          annotationTooltip: display.annotationTooltip,
        }
      : rating;
  }

  const normalizedRating: Rating = {
    ...rating,
    code: display.code,
    isPotentialA0: display.isPotentialA0,
    annotation: display.annotation,
    annotationTooltip: display.annotationTooltip,
  };

  if (display.code !== sourceCode) {
    normalizedRating.oldRatingCode = rating.oldRatingCode ?? rating.code;
  }

  return normalizedRating;
}

function getTransparentRatingColor(color?: string) {
  if (!color) return color;
  if (!HEX_COLOR_PATTERN.test(color)) return null;

  let hex = color.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex.replace(/./g, (char) => char + char);
  }

  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${TRANSPARENT_RATING_ALPHA})`;
}

function getNumericRatingEntries() {
  return Array.from(BerRatings.entries())
    .filter(([code]) => !isIrlActive() || !IRL_NUMERIC_RATING_EXCLUDED_CODES.has(code))
    .sort((a, b) => a[1].level - b[1].level);
}

function getSortedRatingLevels() {
  return Array.from(BerRatings.values())
    .map((ratingInfo) => ratingInfo.level)
    .sort((a, b) => a - b);
}

function getDisplayRatingLevels(levels: number[]) {
  const openEndedDisplayValue = getOpenEndedRatingDisplayMaxValue(levels);
  return levels.map((level) => Number.isFinite(level) ? level : openEndedDisplayValue);
}

function getOpenEndedRatingDisplayMaxValue(levels: number[]) {
  const finiteLevels = levels.filter(Number.isFinite);
  const maxLevel = finiteLevels[finiteLevels.length - 1] ?? 0;
  const previousLevel = finiteLevels[finiteLevels.length - 2] ?? 0;
  return maxLevel + Math.max(maxLevel - previousLevel, 1);
}

function getSortedRatingCodes() {
  return [...BerRatings.keys()].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );
}

function normalizeRatingCode(code?: string) {
  return code?.trim().toUpperCase() ?? "";
}

function getRatingSourceCode(rating?: Rating | null) {
  return rating?.code || rating?.oldRatingCode;
}

function getReportRatingDisplayCode(
  rating: Rating | null | undefined,
  sourceCode: string | undefined,
  sourceFormat: RatingSourceFormat
) {
  if (!isIrlActive()) return getDisplayRatingCode(sourceCode);
  if (sourceFormat !== "legacy") return getDisplayRatingCode(sourceCode);
  return rating && Number.isFinite(rating.value)
    ? getRating(rating.value)
    : simplifyIrlBerRatingCode(sourceCode);
}

function isIrlActive() {
  return activeCountry === AvailableCountryISO3.IRL;
}

function isLegacyIrlSubRating(code: string) {
  return LEGACY_IRL_SUB_RATING_PATTERN.test(code);
}

function canShowPotentialA0(
  rating: Rating,
  sourceFormat: RatingSourceFormat,
  dwelling?: string | object | null
): boolean {
  if (!isIrlActive()) return false;
  if (isAchievedA0Rating(rating)) return false;
  if (typeof rating.isPotentialA0 === "boolean") return rating.isPotentialA0;
  if (sourceFormat !== "legacy") return false;
  if (!Number.isFinite(rating.value) || rating.value > POTENTIAL_A0_MAX_PRIMARY_ENERGY) return false;

  return hasNoFossilHeatingFuelCodes(dwelling);
}

function isAchievedA0Rating(rating?: Rating | null) {
  return normalizeRatingCode(rating?.code) === IRL_A0_RATING_CODE;
}
